<template>
  <div class="instruments">
    <div class="header-row">
      <div class="title-wrap">
        <h3>Instruments</h3>
        <span class="meta-count">{{ (instruments || []).length }} item(s)</span>
      </div>
    </div>

    <div class="instrument-table-wrap">
      <table class="instrument-table">
        <thead>
          <tr>
            <th>Instrument Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="instrument in instruments"
            :key="instrument.id"
            @click="navigateToInstrumentDetail(instrument.id)"
            class="instrument-row"
          >
            <td>{{ instrument.name }}</td>
            <td>
              <button
                class="remove-btn"
                @click.stop="removeInstrument(instrument)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          <tr v-if="!instruments || instruments.length === 0">
            <td colspan="2" class="empty-state">No instruments linked to this experiment yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="add-instrument-controls">
      <select v-model="selectedInstrumentId">
        <option disabled :value="null">Select an instrument…</option>
        <option v-for="instrument in availableInstruments" :value="instrument.id" :key="instrument.id">
          {{ instrument.name }}
        </option>
      </select>
      <button type="button" @click="addInstrument" :disabled="!selectedInstrumentId">Add Instrument</button>
      <button
        type="button"
        class="qr-scan-btn"
        @click="openScanner"
        :disabled="scannerBusy"
        title="Scan instrument QR code"
        aria-label="Scan instrument QR code"
      >
        <i class="bi bi-qr-code-scan"></i>
      </button>
    </div>

    <Teleport to="body">
      <div v-if="scannerOpen" class="scanner-overlay" @click.self="closeScanner">
        <div class="scanner-modal">
          <div class="scanner-header">
            <h4>Scan instrument QR code</h4>
            <button type="button" class="close-btn" @click="closeScanner" aria-label="Close scanner">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="scanner-body">
            <video ref="scannerVideo" class="scanner-video" autoplay playsinline muted></video>
            <p class="scanner-help">Point the camera at an instrument QR code.</p>
            <p v-if="scannerError" class="scanner-error">{{ scannerError }}</p>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="scanConfirmation" class="scanner-overlay" @click.self="cancelScanConfirmation">
        <div class="confirm-modal">
          <h4>Confirm instrument</h4>
          <p>
            Do you want to add instrument
            <strong>{{ scanConfirmation.id }}.{{ scanConfirmation.name }}</strong>?
          </p>
          <div class="confirm-actions">
            <button type="button" class="secondary-btn" @click="cancelScanConfirmation">Cancel</button>
            <button type="button" class="primary-btn" @click="confirmScannedInstrument">Add instrument</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { useToast } from "vue-toastification";
import axios from "axios";
import { API_ENDPOINTS } from "@/services/config";
import apiService from "@/services/apiService";
import { confirmDelete } from "@/utils/confirmDelete";
import { createEdge, deleteEdge, resolveRelationTypeId } from "@/utils/graphEdges";

export default {
  props: {
    instruments: {
      type: Array,
      required: true,
    },
    experimentId: {
      type: Number,
      required: true,
    },
    experimentLocationId: {
      type: Number,
      required: true,
    },
    refreshInstruments: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      availableInstruments: [],
      selectedInstrumentId: null,
      scannerOpen: false,
      scannerBusy: false,
      scannerError: "",
      scanConfirmation: null,
      scannerStream: null,
      scannerAnimationFrame: null,
      barcodeDetector: null,
      scannedInstrumentId: null,
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapState(["loggedInUser"]),
  },
  watch: {
    instruments: {
      handler() {
        this.fetchAvailableInstruments();
      },
      deep: true,
    },
    experimentLocationId() {
      this.fetchAvailableInstruments();
    },
  },
  created() {
    this.fetchAvailableInstruments();
  },
  beforeUnmount() {
    this.stopScanner();
  },
  methods: {
    async fetchAvailableInstruments() {
      try {
        const response = await apiService.fetchResources(
          API_ENDPOINTS.locationInstruments(this.experimentLocationId)
        );

        const addedInstrumentIds = this.instruments.map((inst) => inst.id);
        this.availableInstruments = response.filter(
          (instrument) => !addedInstrumentIds.includes(instrument.id)
        );
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          this.toast.error("Failed to fetch available instruments.");
          console.error("Error fetching instruments:", error);
        }
      }
    },
    async addInstrument() {
      try {
        await this.addInstrumentById(this.selectedInstrumentId);
      } catch (error) {
        this.toast.error("Failed to add instrument.");
        console.error("Error adding instrument:", error);
      }
    },
    async addInstrumentById(instrumentId) {
      const relationTypeId = await resolveRelationTypeId("uses_instrument");
      await createEdge({
        srcNodeId: this.experimentId,
        dstNodeId: Number(instrumentId),
        relationTypeId,
        entraId: this.loggedInUser?.entra_id,
      });
      this.toast.success("Instrument added successfully.");
      this.selectedInstrumentId = null;
      await this.refreshInstruments();
      await this.fetchAvailableInstruments();
    },
    async removeInstrument(instrument) {
      try {
        const isConfirmed = await confirmDelete(
          `Are you sure you want to remove instrument "${instrument.name}" from this experiment?`
        );
        if (!isConfirmed) return;

        await deleteEdge(instrument.edge_id, this.loggedInUser?.entra_id);
        await this.refreshInstruments();
        await this.fetchAvailableInstruments();
        this.toast.success("Instrument removed successfully.");
      } catch (error) {
        this.toast.error("Failed to remove instrument.");
        console.error("Error removing instrument:", error);
      }
    },
    navigateToInstrumentDetail(instrumentId) {
      this.$router.push({ name: "InstrumentDetailPage", params: { id: instrumentId } });
    },
    async openScanner() {
      if (this.scannerBusy) return;

      if (!navigator.mediaDevices?.getUserMedia) {
        this.toast.error("Camera access is not supported in this browser.");
        return;
      }

      if (typeof window.BarcodeDetector === "undefined") {
        this.toast.error("QR scanning is not supported in this browser.");
        return;
      }

      this.scannerBusy = true;
      this.scannerError = "";
      this.scannedInstrumentId = null;

      try {
        this.barcodeDetector = new window.BarcodeDetector({ formats: ["qr_code"] });
        this.scannerStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });

        this.scannerOpen = true;
        await this.$nextTick();

        const video = this.$refs.scannerVideo;
        if (!video) {
          throw new Error("Scanner video element not available.");
        }

        video.srcObject = this.scannerStream;
        await video.play();
        this.scanVideoFrame();
      } catch (error) {
        this.scannerError = "Unable to start camera scanning.";
        this.toast.error("Unable to start camera scanning.");
        console.error("Error starting QR scanner:", error);
        this.closeScanner();
      } finally {
        this.scannerBusy = false;
      }
    },
    closeScanner() {
      this.scannerOpen = false;
      this.scannerError = "";
      this.stopScanner();
    },
    stopScanner() {
      if (this.scannerAnimationFrame) {
        cancelAnimationFrame(this.scannerAnimationFrame);
        this.scannerAnimationFrame = null;
      }

      const video = this.$refs.scannerVideo;
      if (video) {
        video.pause();
        video.srcObject = null;
      }

      if (this.scannerStream) {
        this.scannerStream.getTracks().forEach((track) => track.stop());
        this.scannerStream = null;
      }
    },
    scanVideoFrame() {
      const video = this.$refs.scannerVideo;
      if (!this.scannerOpen || !video || !this.barcodeDetector) {
        return;
      }

      this.scannerAnimationFrame = requestAnimationFrame(async () => {
        try {
          if (video.readyState >= 2) {
            const codes = await this.barcodeDetector.detect(video);
            if (Array.isArray(codes) && codes.length > 0) {
              const rawValue = codes[0]?.rawValue || "";
              await this.handleScannedCode(rawValue);
              return;
            }
          }
        } catch (error) {
          console.error("QR scan frame failed:", error);
        }

        this.scanVideoFrame();
      });
    },
    parseInstrumentIdFromQr(rawValue) {
      if (!rawValue) return null;

      try {
        const url = new URL(rawValue);
        const match = url.pathname.match(/\/instruments\/(\d+)\/?$/i);
        return match ? Number(match[1]) : null;
      } catch (error) {
        const match = String(rawValue).match(/\/instruments\/(\d+)\/?$/i);
        return match ? Number(match[1]) : null;
      }
    },
    async handleScannedCode(rawValue) {
      const instrumentId = this.parseInstrumentIdFromQr(rawValue);

      if (!Number.isFinite(instrumentId)) {
        this.scannerError = "The scanned QR code is not a valid instrument URL.";
        return;
      }

      if (this.instruments.some((instrument) => Number(instrument.id) === instrumentId)) {
        this.toast.info("This instrument is already linked to the experiment.");
        this.closeScanner();
        return;
      }

      try {
        const instrument = await this.fetchInstrumentForConfirmation(instrumentId);
        if (!instrument) {
          this.scannerError = "The scanned instrument could not be found.";
          return;
        }

        const isAvailable = this.availableInstruments.some((item) => Number(item.id) === instrumentId);
        if (!isAvailable) {
          this.scannerError = "This instrument is not available to add to the experiment.";
          return;
        }

        this.scannedInstrumentId = instrumentId;
        this.scanConfirmation = {
          id: instrumentId,
          name: instrument.name || "Unnamed instrument",
        };
        this.closeScanner();
      } catch (error) {
        this.scannerError = "Failed to look up the scanned instrument.";
        console.error("Failed to resolve scanned instrument:", error);
      }
    },
    async fetchInstrumentForConfirmation(instrumentId) {
      const matchingInstrument = this.availableInstruments.find(
        (instrument) => Number(instrument.id) === instrumentId
      );
      if (matchingInstrument) {
        return matchingInstrument;
      }

      const response = await axios.get(API_ENDPOINTS.instrument(instrumentId), {
        params: {
          entra_id: this.loggedInUser?.entra_id,
        },
      });
      return response.data;
    },
    cancelScanConfirmation() {
      this.scanConfirmation = null;
      this.scannedInstrumentId = null;
    },
    async confirmScannedInstrument() {
      if (!Number.isFinite(this.scannedInstrumentId)) {
        this.cancelScanConfirmation();
        return;
      }

      try {
        await this.addInstrumentById(this.scannedInstrumentId);
        this.cancelScanConfirmation();
      } catch (error) {
        this.toast.error("Failed to add instrument.");
        console.error("Error adding scanned instrument:", error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.instruments {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 16px;
  }

  .title-wrap {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  h3 {
    margin: 0;
    color: $primary-color;
  }

  .meta-count {
    font-size: 0.92rem;
    font-weight: 500;
    color: rgba($primary-color, 0.8);
  }

  .instrument-table-wrap {
    border: 1px solid rgba($primary-color, 0.12);
    border-radius: 12px;
    overflow: hidden;
    background: rgba(#fff, 0.65);
    margin-bottom: 16px;
  }

  .instrument-table {
    width: 100%;

    th:last-child,
    td:last-child {
      width: 84px;
    }

    thead th {
      background: rgba($primary-color, 0.06);
    }

    tr:hover {
      background-color: $form-color;
    }

    .instrument-row {
      cursor: pointer;
    }

    .empty-state {
      text-align: center;
      color: rgba($primary-color, 0.66);
      font-style: italic;
    }

    .remove-btn {
      background: none;
      border: none;
      cursor: pointer;
      margin-left: auto;

      i {
        color: $primary-color;
        font-size: 1.2rem;

        &:hover {
          color: darken($primary-color, 10%);
        }
      }
    }
  }

  .add-instrument-controls {
    display: flex;
    align-items: stretch;
    gap: 10px;

    select {
      flex: 1;
      min-width: 0;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid rgba($primary-color, 0.2);
      background: rgba(#fff, 0.84);
    }

    button {
      background-color: $primary-color;
      color: #fff;
      padding: 10px 14px;
      border: none;
      border-radius: 10px;
      cursor: pointer;

      &:hover {
        background-color: darken($primary-color, 10%);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .qr-scan-btn {
      min-width: 46px;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 1.15rem;
      }
    }
  }
}

.scanner-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 22, 34, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  padding: 20px;
}

.scanner-modal,
.confirm-modal {
  width: min(100%, 520px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.24);
  padding: 18px;
}

.scanner-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  h4 {
    margin: 0;
    color: $primary-color;
  }
}

.close-btn {
  border: none;
  background: transparent;
  color: $primary-color;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.scanner-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scanner-video {
  width: 100%;
  border-radius: 12px;
  background: #000;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.scanner-help,
.scanner-error,
.confirm-modal p {
  margin: 0;
}

.scanner-help {
  color: rgba($primary-color, 0.76);
}

.scanner-error {
  color: #b42318;
}

.confirm-modal {
  display: flex;
  flex-direction: column;
  gap: 14px;

  h4 {
    margin: 0;
    color: $primary-color;
  }
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.secondary-btn,
.primary-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
}

.secondary-btn {
  background: rgba($primary-color, 0.12);
  color: $primary-color;
}

.primary-btn {
  background: $primary-color;
  color: #fff;
}
</style>
  